// src/router/index.js

import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
import store from '@/state/store';
import Swal from 'sweetalert2';
import { requestActivity } from '@/services/requestActivity';
// import apiClient from '@/services/api';

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// Authentication and Menu Permission Guard
router.beforeEach(async (routeTo, routeFrom, next) => {
    if (requestActivity.importActive) {
      window.alert('A data import is still running. Keep this page open until it completes.');
      return next(false);
    }

    // const publicPages = ['/login', '/register', '/forgot-password'];
    const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
    const loggeduser = localStorage.getItem('user');
    const jwtToken = localStorage.getItem('jwt');

    // If route requires auth and user is not logged in, redirect to login
    if (authRequired && (!loggeduser || !jwtToken)) {
      return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
    }

    // If user is logged in and trying to access login page, redirect to home
    if (loggeduser && jwtToken && routeTo.path === '/login') {
      return next('/');
    }

    // Check menu permissions if route has menuId
    const menuId = routeTo.meta.menuId;
    if (menuId && loggeduser && jwtToken) {
      try {
        // Ensure menus are loaded
        if (!store.getters['menuAccess/menusLoaded']) {
          console.log('[Router Guard] Loading user menus...');
          await store.dispatch('menuAccess/fetchUserMenus');
        }

        // Check if user can access this menu
        const canAccess = store.getters['menuAccess/canAccessMenu'](menuId);

        if (!canAccess) {
          console.warn(`[Router Guard] Access denied to menu: ${menuId}`);
          // A silent bounce to the dashboard reads as "the page does not exist".
          // Say what happened, and name the menu id — when the real cause is a
          // route missing from the backend's MENU_IDS rather than a genuine
          // permission gap, that id is the whole diagnosis.
          // `title` is a route-table constant, but the `path` fallback carries URL
          // params — escape rather than trust whichever branch happens to be live.
          const escapeHtml = (value) => String(value).replace(
            /[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;',
                                  '"': '&quot;', "'": '&#39;' }[c]));
          const deniedPageTitle = escapeHtml(routeTo.meta.title || routeTo.path);
          Swal.fire({
            icon: 'error',
            title: 'Access denied',
            html: `You do not have permission to open <strong>${deniedPageTitle}</strong>.`
              + `<br><small class="text-muted">Menu: ${escapeHtml(menuId)}</small>`,
            footer: 'Ask an administrator to grant you access to this page.',
          });
          return next({
            name: 'default',
            query: {
              error: 'unauthorized',
              message: 'You do not have permission to access this page'
            }
          });
        }
      } catch (error) {
        console.error('[Router Guard] Error checking menu permission:', error);
        // On error, allow navigation but log the issue
        // This prevents blocking navigation if API fails
      }
    }

    next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
